/**
 * Composable for skill damage/impact calculations
 * Pure functions for calculating skill impact values based on character stats
 */

import type {
  Formula,
  Impact,
  DamageCalculationResult,
  ImpactChartData,
  SkillChartConfig,
  SkillForVisualization
} from '@/types/skillVisualization'
import { VIOLATION_TYPE_COLORS } from '@/types/skillVisualization'

/**
 * Calculate impact value for a given stat value and dice multiplier
 * Formula: base + min(sum(scaling * stat), maxStatBonus) * diceMultiplier
 */
export function calculateImpactValue(
  formula: Formula,
  statValue: number,
  diceMultiplier: number,
  characterStats: Record<string, number> = {}
): number {
  const { base, scaling = [], max_efficiency, min_efficiency } = formula

  // Calculate stat bonus from scaling
  let statBonus = 0
  for (const scale of scaling) {
    const charStatValue = characterStats[scale.stat] || statValue // Use provided stat or default
    statBonus += scale.value * charStatValue
  }

  // Apply efficiency caps if defined
  let efficiency = diceMultiplier
  if (max_efficiency !== undefined) {
    efficiency = Math.min(efficiency, max_efficiency)
  }
  if (min_efficiency !== undefined) {
    efficiency = Math.max(efficiency, min_efficiency)
  }

  // Calculate final value
  const value = base + statBonus * efficiency

  return Math.max(0, Math.round(value * 10) / 10) // Round to 1 decimal, minimum 0
}

/**
 * Calculate min/avg/max values for an impact across a range of stat values
 */
export function calculateImpactRange(
  impact: Impact,
  config: SkillChartConfig,
  characterStats: Record<string, number> = {}
): {
  statValues: number[]
  minValues: number[]
  avgValues: number[]
  maxValues: number[]
} {
  const { minStat, maxStat, statStep, diceMin, diceMax } = config
  const statValues: number[] = []
  const minValues: number[] = []
  const avgValues: number[] = []
  const maxValues: number[] = []

  // Generate stat range from minStat to maxStat
  for (let stat = minStat; stat <= maxStat; stat += statStep) {
    statValues.push(stat)

    // Calculate for min dice roll
    const minValue = calculateImpactValue(impact.formula, stat, diceMin, characterStats)
    minValues.push(minValue)

    // Calculate for average dice roll
    const avgMultiplier = (diceMin + diceMax) / 2
    const avgValue = calculateImpactValue(impact.formula, stat, avgMultiplier, characterStats)
    avgValues.push(avgValue)

    // Calculate for max dice roll
    const maxValue = calculateImpactValue(impact.formula, stat, diceMax, characterStats)
    maxValues.push(maxValue)
  }

  return { statValues, minValues, avgValues, maxValues }
}

/**
 * Generate chart data for all impacts in a skill
 */
export function generateSkillChartData(
  skill: SkillForVisualization,
  config: SkillChartConfig,
  characterStats: Record<string, number> = {}
): ImpactChartData[] {
  return skill.impact.map((impact, index) => {
    // Generate label
    const impactLabel = `${impact.kind}`
    const violationLabel = impact.type !== 'None' ? ` (${impact.type})` : ''
    const label = `${skill.name} - ${impactLabel}${violationLabel}`

    // Get color based on violation type (with fallback for unknown types)
    const color = VIOLATION_TYPE_COLORS[impact.type] || { r: 200, g: 200, b: 200 }

    // Log warning if violation type is unknown
    if (!VIOLATION_TYPE_COLORS[impact.type]) {
      console.warn(
        `[useSkillCalculator] Unknown violation type: "${impact.type}" for skill "${skill.name}". ` +
        `Available types: ${Object.keys(VIOLATION_TYPE_COLORS).join(', ')}`
      )
    }

    // Calculate data
    const data = calculateImpactRange(impact, config, characterStats)

    return {
      impactIndex: index,
      impact,
      label,
      color,
      data
    }
  })
}

/**
 * Check if a character meets stat requirements for an impact
 */
export function meetsRequirements(
  formula: Formula,
  characterStats: Record<string, number>
): { meets: boolean; missing: string[] } {
  const { requires = [] } = formula
  const missing: string[] = []

  for (const requirement of requires) {
    const statValue = characterStats[requirement.stat] || 0
    if (statValue < requirement.value) {
      missing.push(`${requirement.stat}: ${requirement.value} (current: ${statValue})`)
    }
  }

  return {
    meets: missing.length === 0,
    missing
  }
}

/**
 * Calculate total cost of using a skill
 */
export function calculateSkillCost(skill: SkillForVisualization): {
  health: number
  energy: number
  actionPoints: number
} {
  const cost = {
    health: 0,
    energy: 0,
    actionPoints: 0
  }

  if (!skill.cost) return cost

  for (const c of skill.cost) {
    switch (c.kind) {
      case 'Health':
        cost.health += c.value
        break
      case 'Energy':
        cost.energy += c.value
        break
      case 'Action Points':
        cost.actionPoints += c.value
        break
    }
  }

  return cost
}

/**
 * Get primary scaling stat for an impact (the stat with highest scaling value)
 */
export function getPrimaryScalingStat(formula: Formula): string | null {
  if (!formula.scaling || formula.scaling.length === 0) {
    return null
  }

  const sorted = [...formula.scaling].sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
  return sorted[0].stat
}

/**
 * Format impact value for display
 */
export function formatImpactValue(value: number, decimals: number = 1): string {
  return value.toFixed(decimals)
}

export default function useSkillCalculator() {
  return {
    calculateImpactValue,
    calculateImpactRange,
    generateSkillChartData,
    meetsRequirements,
    calculateSkillCost,
    getPrimaryScalingStat,
    formatImpactValue
  }
}
